import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { BarChart, ChartsReferenceLine, LineChart } from "@mui/x-charts";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import { useNavigate, useParams } from "react-router-dom";
import {
  acceptSync,
  rejectSync,
  getSyncResults,
  loadClip,
} from "../utils/dataLoader";
import { pluralizeNum } from "../utils/numHelpers";
import { WaitDialog } from "../../../components/dialogs";

export default function SyncResultsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { caseId, clipId } = useParams();
  const [loaded, setIsLoaded] = useState(false);
  const [clip, setClip] = useState(null);

  const handleAccept = async () => {
    setIsLoaded(false);
    await acceptSync(caseId, clipId);
    setIsLoaded(true);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const handleRevert = async () => {
    setIsLoaded(false);
    await rejectSync(caseId, clipId);
    setIsLoaded(true);
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const [syncResults, setSyncResults] = useState(null);
  const [syncInput, setSyncInput] = useState(null);
  const [syncRange, setSyncRange] = useState([-0.05, 1]);
  const hasFailedSyncIndicator = syncResults?.channels.some(
    (item) => item.syncIndicator === false,
  );

  const xAxisFormatter = (samples) => {
    let val = (samples / syncInput?.sampleRate) * 1000;
    let safeVal = isFinite(val) ? Math.trunc(val) : 0;
    return `${safeVal}`;
  };

  const tooltipFormatter = (samples) => {
    let val = (samples / syncInput?.sampleRate) * 1000;
    let safeVal = isFinite(val) ? Math.trunc(val) : 0;
    return `Offset: ${safeVal}ms (${samples} sample${pluralizeNum(samples)})`;
  };

  useEffect(() => {
    const fetchSyncResults = async () => {
      const currClip = await loadClip(caseId, clipId);
      setClip(currClip);

      const results = await getSyncResults(caseId, clipId);
      setSyncResults(results.syncResults);
      setSyncInput(results.inputConfig);
      const maxMetric = 1;
      // const maxMetric = Math.max(
      //   ...results.syncResults.channels.flatMap((channel) =>
      //     channel.metrics.map((v) => v[1])
      //   )
      // );
      setSyncRange([-0.05, maxMetric]);
      setIsLoaded(true);
    };

    fetchSyncResults();
  }, [caseId, clipId]);

  return (
    <Stack
      component="main"
      height={1}
      // pb allows for scrolling
      pb={10}
      sx={{ overflowY: "auto", overscrollBehavior: "contain" }}
    >
      <WaitDialog open={!loaded} />
      <Stack flex={1} p={2} pb={16} spacing={1}>
        {syncResults && (
          <>
            <Paper
              elevation={4}
              sx={{ p: 2, position: "sticky", top: 0, zIndex: 1 }}
            >
              <Typography textTransform="uppercase" variant="h6">
                Sync Results
              </Typography>
              <Typography textTransform="uppercase" variant="body1">
                Reference Channel:
                <Typography component="span" ml={0.5}>
                  {syncInput.refChannel + 1}
                </Typography>
              </Typography>
              {hasFailedSyncIndicator && (
                <Alert
                  severity="error"
                  sx={{ boxShadow: 1, mt: 1.5, width: "fit-content" }}
                >
                  Time offset align match not found for some channels. Please
                  review the results below.
                </Alert>
              )}
            </Paper>
            {hasFailedSyncIndicator ? (
              <Accordion
                defaultExpanded
                sx={{
                  "&.MuiAccordion-root:before": {
                    backgroundColor: "transparent",
                  },
                  borderRadius: 1,
                  boxShadow: 4,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography
                    textTransform="uppercase"
                    fontWeight={500}
                    variant="body1"
                  >
                    Time Offset Align Match Results
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexWrap="wrap" gap={1} maxWidth={700}>
                    {syncResults.channels.map((item) => (
                      <Stack alignItems="center" key={item.channelNumber}>
                        <Typography variant="caption">
                          CHAN {item.channelNumber + 1}
                        </Typography>
                        <Box
                          borderRadius={2}
                          boxShadow={3}
                          height={60}
                          position="relative"
                          width={60}
                          sx={{
                            background: item.syncIndicator
                              ? `radial-gradient(at top left, ${theme.palette.success.light}, ${theme.palette.success.main})`
                              : `radial-gradient(at top left, ${theme.palette.error.light}, ${theme.palette.error.main})`,
                          }}
                        />
                      </Stack>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Paper elevation={4} sx={{ p: 2 }}>
                <Typography
                  mb={2}
                  fontWeight={500}
                  textTransform="uppercase"
                  variant="body1"
                >
                  Adjustments
                </Typography>
                <BarChart
                  layout="horizontal"
                  borderRadius={10}
                  height={400}
                  grid={{ vertical: true }}
                  series={[
                    {
                      data: syncResults?.sampleAdjustments.map(
                        (adj) =>
                          adj -
                          syncResults.sampleAdjustments[syncInput.refChannel],
                      ),
                      valueFormatter: tooltipFormatter,
                    },
                  ]}
                  xAxis={[
                    {
                      valueFormatter: xAxisFormatter,
                      domainLimit: (min, max) => {
                        const absMax = Math.max(Math.abs(min), Math.abs(max));
                        return { min: -absMax, max: absMax };
                      },
                      label: "MILLISECONDS",
                      colorMap: {
                        type: "piecewise",
                        thresholds: [0],
                        colors: [
                          theme.palette.warning.light,
                          theme.palette.info.dark,
                        ],
                      },
                    },
                  ]}
                  yAxis={[
                    {
                      data: syncResults?.sampleAdjustments.map(
                        (_, index) => `${index + 1}`,
                      ),
                      label: "CHANNELS",
                      tickInterval: (value, index) => {
                        if (syncResults?.sampleAdjustments.length > 20) {
                          // Show fewer ticks for larger amount of channels
                          return index %
                            Math.ceil(
                              syncResults?.sampleAdjustments.length / 20,
                            ) ===
                            0
                            ? value
                            : null;
                        }
                        return value;
                      },
                      valueFormatter: (value, context) =>
                        context?.location === "tooltip"
                          ? `Channel ${value}`
                          : value,
                    },
                  ]}
                  margin={{ left: 0 }}
                >
                  <ChartsReferenceLine
                    x={0}
                    lineStyle={{
                      shapeRendering: "crispEdges",
                      stroke: "rgba(0, 0, 0, .5)",
                      strokeWidth: 2.25,
                    }}
                  />
                </BarChart>
              </Paper>
            )}

            <Accordion
              sx={{
                "&.MuiAccordion-root:before": {
                  backgroundColor: "transparent",
                },
                borderRadius: 1,
                boxShadow: 4,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography
                  textTransform="uppercase"
                  fontWeight={500}
                  variant="body1"
                >
                  Detailed Results
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {syncResults && (
                  <Grid container spacing={2}>
                    {syncResults.channels.map((channel, index) => (
                      <Grid
                        key={index}
                        borderRadius={1}
                        boxShadow={2}
                        display="flex"
                        flexDirection="column"
                        height={400}
                        outline={
                          hasFailedSyncIndicator && !channel.syncIndicator
                            ? `1px solid ${theme.palette.error.main}`
                            : "none"
                        }
                        overflow="hidden"
                        p={3}
                        size={{
                          xs: 12,
                          md: 6,
                          xl: 4,
                        }}
                      >
                        <Typography
                          gutterBottom
                          textTransform="uppercase"
                          variant="caption"
                        >
                          Channel {index + 1}{" "}
                          {index + 1 === syncInput.refChannel + 1 &&
                            "(Reference)"}
                        </Typography>
                        <LineChart
                          grid={{ vertical: true, horizontal: true }}
                          margin={{ bottom: 0, left: -8 }}
                          xAxis={[
                            {
                              data: channel.metrics.map((v) => v[0]),
                              label: "TIME OFFSET (SECONDS)",
                            },
                          ]}
                          yAxis={[
                            {
                              domainLimit: () => ({
                                min: syncRange[0],
                                max: syncRange[1],
                              }),
                            },
                          ]}
                          series={[
                            {
                              data: channel.metrics.map((v) => v[1]),
                              showMark: false,
                              color: hasFailedSyncIndicator
                                ? channel.syncIndicator
                                  ? theme.palette.info.main
                                  : theme.palette.error.main
                                : theme.palette.info.main,
                            },
                          ]}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
      <Paper
        elevation={4}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          position: "fixed",
          transform: "translateX(-50%)",
          bottom: 64,
          mb: 1,
          left: "50%",
          zIndex: 1,
          width: "calc(100% - 1rem)",
        }}
      >
        <Button
          aria-label="Revert Changes"
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={handleRevert}
        >
          Revert Changes
        </Button>
        <Button
          aria-label="Accept Results"
          disabled={syncResults && hasFailedSyncIndicator}
          loading={!syncResults}
          variant="contained"
          onClick={handleAccept}
        >
          Accept Results
        </Button>
      </Paper>
      <Box position="fixed" bottom={0} width={1} zIndex={1}>
        <ContentTitleBar
          name={clip?.description}
          label={clip?.afCase?.caseName}
          onBack={() => navigate(`/app/cases/${caseId}/clips`)}
        />
      </Box>
    </Stack>
  );
}
